<template>
  <div ref="container" class="grading-rubric-criterion-view grading-rubric-criteria">
    <div class="criteria-row">
      <h5 class="title">{{ showCriteriaDescription }}</h5>
      <p v-if="showMarkRange" class="marks">
        {{ showCriteriaMarksLabel }}
      </p>
    </div>
    <match-indicator v-if="matchBandDescription" />
    <div class="criteria-header">
      <cv-content-switcher :id="contentSwitcherId" @selected="handleSectionSelected">
        <content-switcher-button
          v-for="(criteriaRange, index) in criteriaContent.criteriaRangesData"
          :key="populateRangeKey(criteriaRange, index)"
          class="mark-range"
          :class="{
            '_show-marks': showMarkRange,
          }"
          :parent-switcher="contentSwitcherId"
          :owner-id="populateOwnerId(criteriaRange)"
          :selected="isSectionSelected(criteriaRange)"
        >
          <template v-if="showMarkRange">
            {{ populateMarkRangeTitle(criteriaRange) }}
          </template>
        </content-switcher-button>
      </cv-content-switcher>
    </div>

    <div class="criteria-content">
      <cv-content-switcher-content
        v-for="(criteriaRange, index) in criteriaContent.criteriaRangesData"
        :key="populateRangeKey(criteriaRange, index)"
        :parent-switcher="contentSwitcherId"
        :owner-id="populateOwnerId(criteriaRange)"
      >
        <rich-text-card
          :title="populateDescriptionCardTitle(criteriaRange, index)"
          :content="criteriaRange.description"
          :visible="isSectionSelected(criteriaRange)"
          class="criteria-card"
        >
          <template v-if="isQuizGradingRubric" #info>
            <filter-tag-list v-if="true">
              <filter-tag
                v-for="(tag, tagIndex) in criteriaContent.selectedQuestionTags"
                :key="tagIndex"
                :label="tag.label"
              />
            </filter-tag-list>
          </template>
          <template v-else-if="showMarkRange" #info>
            <div class="marks">
              {{ t("marks_label") }} {{ populateMarkRangeTitle(criteriaRange) }}
            </div>
          </template>
        </rich-text-card>
      </cv-content-switcher-content>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch } from "vue";
import { useI18n } from "vue-i18n";
import type { CriteriaContent } from "@modules/grading-rubric/types.ts";

const { t } = useI18n();

// Props Interface
interface Props {
  gradingRubricUuid?: string | null;
  criteriaLabel?: string | null;
  criteriaContent?: CriteriaContent;
  showMarkRange?: boolean;
  visible?: boolean;
  expanded?: boolean;
  isQuizGradingRubric?: boolean;
  matchBandDescription?: boolean;
  expandFirstBandForAll?: boolean;
}

// Data
const currentCriteriaSelected = ref("");

// Props
const props = withDefaults(defineProps<Props>(), {
  gradingRubricUuid: null,
  criteriaLabel: null,
  criteriaContent: () => ({
    criteriaDescription: "",
    criteriaMarks: 0,
    criteriaRangesData: [],
    selectedQuestionTags: [],
  }),
  showMarkRange: false,
  visible: true,
  expanded: false,
  isQuizGradingRubric: false,
  matchBandDescription: false,
  expandFirstBandForAll: false,
});

// Computed
const showCriteriaDescription = computed(() => {
  return t("grading_rubric_criteria_label_description", {
    criteriaLabel: props.criteriaLabel,
    criteriaDescription: props.criteriaContent.criteriaDescription,
  });
});

const showCriteriaMarksLabel = computed(() => {
  return t("grading_rubric_criteria_label_mark", {
    criteriaMarks: props.criteriaContent.criteriaMarks,
  });
});

const isSectionSelected = computed(() => {
  return (value) => {
    const ownerId = populateOwnerId(value);
    return props.visible && ownerId === currentCriteriaSelected.value;
  };
});

const contentSwitcherId = computed(() => {
  const criteriaSeed =
    props.criteriaContent.criteriaRangesData?.[0]?.criteriaIndex ?? props.criteriaLabel;
  const uuid = props.expandFirstBandForAll
    ? criteriaSeed
    : `${props.gradingRubricUuid}_${criteriaSeed}`;
  return `content_switcher_grading_rubric_criterion_view_${uuid}`;
});

// Methods
const initSelectedMarkRange = () => {
  if (props.expandFirstBandForAll) {
    const firstBand = props.criteriaContent.criteriaRangesData?.[0];
    if (firstBand) {
      currentCriteriaSelected.value = populateOwnerId(firstBand);
    }
  } else {
    const temp = currentCriteriaSelected.value;
    currentCriteriaSelected.value = "";
    nextTick(() => {
      currentCriteriaSelected.value = temp;
    });
  }
};

const populateMarkRangeTitle = (criteriaRange) => {
  const min = criteriaRange.minScore;
  const max = criteriaRange.maxScore;
  if (min === max) {
    return `${max}`;
  }
  return `${min} ${t("to_small_letter_case")} ${max}`;
};

const populateDescriptionCardTitle = (value, index) => {
  const headerIndex = index + 1;
  return `${headerIndex}. ${value.headerDescription}`;
};

const populateOwnerId = (value) => {
  return `${value.gradingRubricCriteriaId}_${value.gradingRubricRangeId}`;
};

const populateRangeKey = (value, index) => {
  return `${populateOwnerId(value)}_${index}`;
};

const handleSectionSelected = (selected) => {
  currentCriteriaSelected.value = selected;
};

// Watch
watch(
  () => props.expanded,
  () => {
    initSelectedMarkRange();
  },
);

// Created
if (props.expanded) {
  initSelectedMarkRange();
}
</script>
